import {
  AGENT_MEDIA_LIMITS,
  mediaKindForMimeType,
  PRODUCT_MEDIA_FILE_KIND,
  type AgentMediaKind,
  type ProductMediaKind,
} from "@school-sales-ai/shared";

/**
 * The browser-side check of a file the agent will send — shared by the upload
 * form and the "reemplazar archivo" field of the edit modal, so a replacement
 * can never be held to a looser rule than a first upload. Same catalogue the
 * server validates against; only the video CODEC is server-only, and its
 * message arrives through the request's error.
 */
export function checkAgentMediaFile(file: File | null): {
  kind: AgentMediaKind | null;
  error: string | null;
} {
  if (file === null) return { kind: null, error: null };
  const kind = mediaKindForMimeType(file.type);
  if (kind === null) {
    return { kind: null, error: "Solo se permiten archivos PDF, JPG, PNG o MP4." };
  }
  const { label, maxSizeMb } = AGENT_MEDIA_LIMITS[kind];
  if (file.size > maxSizeMb * 1024 * 1024) {
    return { kind, error: `${label}: el archivo supera el límite de ${maxSizeMb} MB.` };
  }
  return { kind, error: null };
}

/** What a product slot says when handed a file of another kind — the server's wording. */
const ONLY_KIND_ERROR: Record<ProductMediaKind, string> = {
  gallery: "La galería solo acepta imágenes JPG o PNG.",
  video: "Aquí solo se acepta un video MP4.",
  pdf: "Aquí solo se acepta un PDF.",
};

/** `checkAgentMediaFile`, narrowed to the one kind a product slot accepts. */
export function checkProductSlotFile(file: File, slot: ProductMediaKind): string | null {
  const { kind, error } = checkAgentMediaFile(file);
  if (kind !== PRODUCT_MEDIA_FILE_KIND[slot]) return ONLY_KIND_ERROR[slot];
  return error;
}

/** "PDF máx. 10 MB · JPG o PNG máx. 5 MB · MP4 máx. 16 MB", from the shared caps. */
export const AGENT_MEDIA_LIMITS_HINT = `PDF máx. ${AGENT_MEDIA_LIMITS.document.maxSizeMb} MB · JPG o PNG máx. ${AGENT_MEDIA_LIMITS.image.maxSizeMb} MB · MP4 máx. ${AGENT_MEDIA_LIMITS.video.maxSizeMb} MB`;

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
